import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { drawArchetype } from "@/lib/api";
import { IMAGES } from "@/data/content";
import ShareButton from "@/components/ShareButton";

const ease = [0.22, 1, 0.36, 1];

export const DeckReveal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [result, setResult] = useState(null);

  const draw = useCallback(async () => {
    setLoading(true);
    setFlipped(false);
    setResult(null);
    try {
      const data = await drawArchetype({});
      setResult(data);
      setTimeout(() => setFlipped(true), 450);
    } catch (e) {
      toast.error("The deck is still. Draw again in a moment.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      draw();
    } else {
      setFlipped(false);
      setResult(null);
    }
  }, [open, draw]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[95] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="deck-reveal"
        >
          <div className="absolute inset-0 bg-[#030303]/92 backdrop-blur-xl" onClick={onClose} />
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-[72vh] w-[72vh] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#3B2A48]/30 blur-[130px]" />

          <button
            onClick={onClose}
            className="absolute right-6 top-6 z-10 text-white/50 transition-colors hover:text-[#C5A059]"
            data-testid="deck-close-btn"
            aria-label="Close the deck"
          >
            <X size={22} strokeWidth={1.5} />
          </button>

          <motion.div
            className="relative z-10 flex flex-col items-center"
            initial={{ scale: 0.94, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.7, ease }}
          >
            <p className="overline mb-7">The Governance Scrying · Draw a Card</p>

            <div className="perspective-1200 h-[58vh] max-h-[600px] w-[290px] md:w-[350px]">
              <motion.div
                className="preserve-3d relative h-full w-full"
                animate={{ rotateY: flipped ? 180 : 0 }}
                transition={{ duration: 1.1, ease }}
              >
                {/* Front — deck back */}
                <div className="backface-hidden absolute inset-0 overflow-hidden rounded-[22px] border border-[#C5A059]/40 shadow-[0_0_80px_rgba(59,42,72,0.7)]">
                  <img src={IMAGES.oracleDeck} alt="Sybils deck" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-[#050505]/55" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="relative flex h-24 w-24 items-center justify-center">
                      <div className="absolute inset-0 rounded-full border border-[#C5A059]/50" />
                      <div className="absolute inset-3 rounded-full border border-[#C5A059]/25" />
                      <Sparkles className="text-[#C5A059] animate-pulse" size={30} strokeWidth={1} />
                    </div>
                    <p className="mt-6 font-serif text-3xl font-light italic text-[#C5A059]">Sybils</p>
                    <p className="overline mt-2 text-[0.55rem]">
                      {loading ? "Scrying the lineage…" : "The Oracle Deck"}
                    </p>
                  </div>
                </div>

                {/* Back — the drawn archetype */}
                <div
                  className="backface-hidden absolute inset-0 overflow-hidden rounded-[22px] border border-[#C5A059]/50 bg-[#0a0a0a] shadow-[0_0_90px_rgba(59,42,72,0.85)]"
                  style={{ transform: "rotateY(180deg)" }}
                  data-testid="deck-result-card"
                >
                  {result && (
                    <div className="flex h-full flex-col">
                      <div className="relative h-1/2 overflow-hidden">
                        <img src={result.image} alt={result.name} className="h-full w-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
                        <span className="absolute right-4 top-4 rounded-full border border-[#C5A059]/40 px-3 py-1 text-[0.6rem] uppercase tracking-[0.2em] text-[#C5A059]">
                          {result.element}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-6">
                        <h3 className="font-serif text-3xl font-light text-[#C5A059]">{result.name}</h3>
                        <p className="overline mt-1 text-[0.56rem]">{result.title}</p>
                        <p className="mt-4 flex-1 overflow-y-auto text-sm font-light italic leading-relaxed text-white/70">
                          “{result.statement}”
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>

            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={draw}
                disabled={loading}
                className="cta-glow inline-flex items-center gap-2 rounded-full bg-[#C5A059] px-8 py-4 text-xs font-medium uppercase tracking-[0.24em] text-[#050505] transition-transform duration-300 hover:-translate-y-1 disabled:opacity-60"
                data-testid="deck-draw-again-btn"
              >
                <RotateCcw size={15} strokeWidth={1.5} />
                {loading ? "Scrying…" : "Draw again"}
              </button>
              {result && !loading && <ShareButton result={result} />}
              <button
                onClick={onClose}
                className="text-xs uppercase tracking-[0.2em] text-white/40 transition-colors hover:text-[#C5A059]"
                data-testid="deck-dismiss-btn"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DeckReveal;
